import TeletLogo from 'src/assets/logo/tellet-logo-main.svg';
import * as Yup from 'yup';
import { SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { IconLoader2, IconUserShield } from '@tabler/icons-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import Cookies from 'js-cookie';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Image from '@/components/atoms/Image';
import axiosInstanceNoAuth from '@/axios/axiosInstanceNoAuth';
import useAuthAdminStore from '@/store/AdminAuthStore';

const defaultValues = {
  email: '',
  password: '',
};

const EmailError = {
  401: 'Invalid email or password',
  500: 'Server error occurred, please try again later',
};

type TLoginForm = typeof defaultValues;

const validationSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email format').required('Email is required'),
  password: Yup.string().required('Password is required'),
});

function Login() {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues, resolver: yupResolver(validationSchema) });
  const location = useLocation();
  const navigate = useNavigate();
  const [loginError, setLoginError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const from = location.state?.from || '/admin/dashboard';
  const authAdminToken = useAuthAdminStore((state) => state.authAdminToken);
  const setAuthAdminToken = useAuthAdminStore((state) => state.setAuthAdminToken);

  const onSubmit: SubmitHandler<TLoginForm> = async (formData: TLoginForm) => {
    setIsLoading(true);
    try {
      const lowercaseEmail = formData.email.toLocaleLowerCase();
      const res = await axiosInstanceNoAuth.post('/admin/login', {
        ...formData,
        email: lowercaseEmail,
      });
      const { data } = res;

      setAuthAdminToken(data?.token);
      Cookies.set('admin_token', data?.token);
      navigate(from, { replace: true });
      setLoginError('');
    } catch (err) {
      const error = err as AxiosError;
      const status = error.status as unknown as keyof typeof EmailError;
      setLoginError(EmailError[status]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (authAdminToken) {
      navigate(from, { replace: true });
    }
  }, [authAdminToken, navigate, from]);

  return (
    <main className="flex items-center justify-center w-full h-full min-h-screen text-center bg-light-pink">
      <div className="w-full max-w-[550px] min-h-screen center border-0 py-4 px-3 min450:max-w-full min450:bg-white">
        <div className="w-full max-w-[550px] flex justify-center">
          <div
            className="flex flex-col items-center w-full ring-1 ring-neutral-200 max-w-[500px] gap-10
           bg-white min450:ring-transparent min450:shadow-none rounded-md shadow-md h-fit p-7 min450:p-5"
          >
            <Image effect="blur" src={TeletLogo} alt="logo" width={120} height={37} />
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="flex flex-col items-start w-full gap-5 h-fit"
            >
              <label className="flex text-[#2a3547] flex-col w-full gap-2 items-start text-sm font-semibold leading-[1.5] relative">
                Email
                <Input {...register('email')} hasError={!!errors.email} type="text" />
                {errors.email?.message && (
                  <p className="absolute top-0 ml-1 text-sm text-red-600 right-2">
                    {errors.email?.message}
                  </p>
                )}
              </label>
              <label className="flex text-[#2a3547] flex-col w-full gap-2 items-start text-sm font-semibold leading-[1.5] relative">
                Password
                <Input {...register('password')} type="password" hasError={!!errors.password} />
                {errors.password?.message && (
                  <p className="absolute top-0 ml-1 text-sm text-red-600 right-2">
                    {errors.password?.message}
                  </p>
                )}
              </label>
              <div className="relative flex flex-col w-full mt-5">
                <Button
                  disabled={isSubmitting || isLoading}
                  variant="default"
                  type="submit"
                  className="flex items-center gap-0.5"
                >
                  {(isLoading || isSubmitting) && (
                    <IconLoader2 className="w-5 h-5 mr-2 animate-spin" />
                  )}
                  <div className="flex items-center gap-1">
                    <IconUserShield size={18} />
                    Log In
                  </div>
                </Button>
                {loginError && (
                  <p className="absolute left-0 text-sm font-medium text-red-600 -top-6">
                    {loginError}
                  </p>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </main>
  );
}

export default Login;
